import { customElement, type TemplateResult, html, property, css, cssManager } from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-multitoggle.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

export type TMultitoggleOption = string | { key: string; label: string; disabled?: boolean };

declare global {
  interface HTMLElementTagNameMap { 'dees-input-multitoggle': DeesInputMultitoggle; }
}

/** Segmented single selection, with string, keyed, and boolean values. */
@customElement('dees-input-multitoggle')
export class DeesInputMultitoggle extends DeesInputBase<DeesInputMultitoggle> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  @property() accessor type: 'boolean' | 'multi' | 'single' = 'multi';
  @property() accessor booleanTrueName = 'true';
  @property() accessor booleanFalseName = 'false';
  @property({ type: Array }) accessor options: TMultitoggleOption[] = [];
  @property() accessor selectedOption = '';
  @property({ type: Boolean }) accessor boolValue = false;
  @property() accessor size: 'default' | 'sm' = 'default';

  public get value(): string | boolean {
    return this.type === 'boolean' ? this.selectedOption === this.booleanTrueName : this.selectedOption;
  }
  public set value(value: string | boolean) {
    if (typeof value === 'boolean') {
      this.boolValue = value;
      this.selectedOption = '';
    } else {
      this.selectedOption = value;
    }
  }

  constructor() {
    super();
    this.registerStartupFunction(async (signal) => {
      await this.updateComplete;
      await document.fonts.ready;
      if (signal.aborted) return;
      this.setIndicator();
      const observer = new ResizeObserver(() => this.setIndicator());
      observer.observe(this.shadowRoot!.querySelector('.selections')!);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  public static styles = [themeDefaultStyles, ...DeesInputBase.baseStyles, cssManager.defaultStyles, css`
    :host { display: block; min-width: 0; font-family: var(--dees-font-family); color: var(--dees-color-text-primary); }
    .selections { position: relative; display: inline-flex; flex-wrap: wrap; align-items: stretch; gap: 2px; max-width: 100%; padding: 2px; border-radius: var(--dees-radius-lg); background: var(--dees-color-fill-tertiary); user-select: none; }
    .option { position: relative; display: grid; place-items: center; flex: 1 0 auto; min-width: 0; max-width: 100%; min-height: calc(var(--dees-input-height) - 4px); padding: 2px 12px; border: 0; border-radius: calc(var(--dees-radius-lg) - 2px); background: transparent; color: var(--dees-color-text-secondary); font: 13px/1.4 var(--dees-font-family); text-align: center; overflow-wrap: anywhere; cursor: pointer; z-index: 1; transition: color var(--dees-transition-fast) var(--dees-ease-standard), background-color var(--dees-transition-fast) var(--dees-ease-standard); }
    .option-label, .option::after { grid-area: 1 / 1; }
    .option::after { content: attr(data-option); visibility: hidden; font-weight: 600; pointer-events: none; }
    .option.selected { color: var(--dees-color-text-primary); font-weight: 600; }
    .option:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 1px; }
    .option:disabled { opacity: .5; cursor: default; }
    .indicator { position: absolute; opacity: 0; border-radius: calc(var(--dees-radius-lg) - 2px); background: var(--dees-color-control-thumb); box-shadow: var(--dees-shadow-xs), inset 0 0 0 1px var(--dees-color-surface-highlight); pointer-events: none; transition: transform var(--dees-transition-fast) var(--dees-ease-standard), width var(--dees-transition-fast) var(--dees-ease-standard), height var(--dees-transition-fast) var(--dees-ease-standard); left: 0; top: 0; }
    .selections[aria-disabled='true'] { opacity: .5; }
    .selections[aria-disabled='true'] .option:disabled { opacity: 1; }
    .small .option { min-height: 24px; padding: 1px 10px; font-size: 12px; }
    @media (hover: hover) { .option:not(:disabled):not(.selected):hover { background: var(--dees-color-hover); color: var(--dees-color-text-primary); } }
    @media (pointer: coarse) { .option, .small .option { min-height: 44px; } }
    @media (prefers-reduced-motion: reduce) { .indicator, .option { transition: none; } }
  `];

  private optionKey(option: TMultitoggleOption): string { return typeof option === 'string' ? option : option.key; }
  private optionLabel(option: TMultitoggleOption): string { return typeof option === 'string' ? option : option.label; }
  private optionDisabled(option: TMultitoggleOption): boolean { return this.disabled || (typeof option === 'object' && !!option.disabled); }

  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (this.type === 'boolean') {
      const oldTrue = String(changed.get('booleanTrueName') ?? this.booleanTrueName);
      const truth = changed.has('selectedOption') && this.selectedOption !== ''
        ? this.selectedOption === oldTrue || this.selectedOption === this.booleanTrueName
        : changed.has('boolValue') ? this.boolValue : this.selectedOption === oldTrue;
      if (this.options.length !== 2 || this.options[0] !== this.booleanTrueName || this.options[1] !== this.booleanFalseName) this.options = [this.booleanTrueName, this.booleanFalseName];
      this.selectedOption = truth ? this.booleanTrueName : this.booleanFalseName;
      this.boolValue = truth;
    } else if (!this.selectedOption && this.options.length) {
      const first = this.options.find(option => !this.optionDisabled(option));
      if (first) this.selectedOption = this.optionKey(first);
    }
  }

  public render(): TemplateResult {
    const focusKey = this.options.some(option => this.optionKey(option) === this.selectedOption && !this.optionDisabled(option))
      ? this.selectedOption : this.options.find(option => !this.optionDisabled(option));
    return html`<div class="input-wrapper">
      ${this.labelPosition !== 'none' && this.label ? this.renderLabel() : ''}
      <div class="selections input-container ${this.size === 'sm' ? 'small' : ''}" role="radiogroup" aria-label=${this.label || this.key || 'Options'} aria-disabled=${this.disabled}>
        <div class="indicator" aria-hidden="true"></div>
        ${this.options.map(option => {
          const key = this.optionKey(option); const label = this.optionLabel(option); const selected = key === this.selectedOption;
          return html`<button type="button" role="radio" class="option ${selected ? 'selected' : ''}" data-option=${label}
            aria-checked=${selected} ?disabled=${this.optionDisabled(option)} tabindex=${key === (typeof focusKey === 'object' ? focusKey.key : focusKey) ? 0 : -1}
            @click=${() => this.handleSelection(key)} @keydown=${this.handleKeydown}><span class="option-label">${label}</span></button>`;
        })}
      </div>${this.renderDescription()}
    </div>`;
  }

  public async updated(changed: Map<string, unknown>) {
    super.updated(changed);
    this.setIndicator();
  }

  public async handleSelection(key: string) {
    const option = this.options.find(option => this.optionKey(option) === key);
    if (!option || this.optionDisabled(option)) return;
    const changed = key !== this.selectedOption;
    this.selectedOption = key;
    if (this.type === 'boolean') this.boolValue = key === this.booleanTrueName;
    this.dispatchEvent(new CustomEvent('option-activate', { detail: { value: this.value, selectedOption: key }, bubbles: true, composed: true }));
    if (!changed) return;
    this.changeSubject.next(this);
    for (const type of ['input', 'change']) this.dispatchEvent(new CustomEvent(type, { detail: { value: this.value, selectedOption: key }, bubbles: true, composed: true }));
    await this.updateComplete;
    this.setIndicator();
  }

  private handleKeydown(event: KeyboardEvent) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const controls = [...this.shadowRoot!.querySelectorAll<HTMLButtonElement>('.option:not(:disabled)')];
    const index = controls.indexOf(event.currentTarget as HTMLButtonElement);
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? controls.length - 1 : (index + (forward ? 1 : -1) + controls.length) % controls.length;
    const control = controls[next];
    if (control) { control.focus(); control.click(); }
  }

  /** Reposition the selected segment after external layout changes. */
  public async setIndicator() {
    const indicator = this.shadowRoot?.querySelector<HTMLElement>('.indicator');
    const option = this.shadowRoot?.querySelector<HTMLElement>('.option.selected');
    if (!indicator) return;
    if (!option) { indicator.style.opacity = '0'; return; }
    Object.assign(indicator.style, { transform: `translate(${option.offsetLeft}px, ${option.offsetTop}px)`, width: `${option.offsetWidth}px`, height: `${option.offsetHeight}px`, opacity: '1' });
  }

  public getValue(): string | boolean { return this.value; }
  public setValue(value: string | boolean): void { this.value = value; }
}
